import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { INTERCOM_ROUTE } from "../../shared/reservedRoutes";
import {
  intercomIframeSandbox,
  type IntercomBootMessage,
  type IntercomNavigateMessage,
  type IntercomReadyMessage,
  type IntercomShowMessage,
  type IntercomThemeMessage,
  type IntercomUnreadMessage,
  type IntercomVisibilityMessage,
} from "../../shared/intercomShell";
import { config } from "../environment";
import { emitErrorToast } from "../lib/errorToast";
import { publicUrl } from "../lib/tenantOrigin";
import { useCurrentLocation } from "../lib/urls";
import { zIndexes } from "../lib/zIndexes";
import { intercomUserJwt } from "./useSessionBootstrap";

// Delivery of the shell's ready message is not ordered against the parent's
// load event (the cross-origin frame's postMessage routes through the browser
// process), so the failure verdict waits out this grace period after load and
// is cancelled if ready arrives.
export const INTERCOM_READY_GRACE_MS = 1000;

const Frame = styled("iframe")`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  z-index: ${zIndexes.intercomMessenger};
`;

type Props = {
  user: { email: string; name: string; tenantId: string };
  open: boolean;
  onClose: () => void;
  onUnreadChange: (count: number) => void;
  onFailedChange: (failed: boolean) => void;
};

function displayedColorScheme(): "light" | "dark" {
  const scheme = document.documentElement.style.colorScheme;
  if (scheme === "light" || scheme === "dark") return scheme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

// The frame boots hidden at sign-in and stays mounted, so replies from
// support arrive (as unread-count messages) even while the messenger is
// closed, and the conversation survives close/reopen.
export function IntercomMessenger({
  user,
  open,
  onClose,
  onUnreadChange,
  onFailedChange,
}: Props) {
  const [visible, setVisible] = useState(false);
  const booted = useRef(false);
  const shellReady = useRef(false);
  const shellFailed = useRef(false);
  const readyVerdictTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appIdMismatchLogged = useRef(false);
  const openRef = useRef(open);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const currentLocation = useCurrentLocation();
  const locationRef = useRef(currentLocation);
  const shellUrl = useMemo(() => `${publicUrl()}${INTERCOM_ROUTE}`, []);
  const shellOrigin = useMemo(() => new URL(shellUrl).origin, [shellUrl]);

  const postShow = useCallback(() => {
    const message: IntercomShowMessage = { type: "3b-intercom-show" };
    frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
  }, [shellOrigin]);

  useEffect(() => {
    openRef.current = open;
    if (open && booted.current) postShow();
  }, [open, postShow]);

  useEffect(() => {
    locationRef.current = currentLocation;
    if (!booted.current) return;
    const message: IntercomNavigateMessage = {
      type: "3b-intercom-navigate",
      path: currentLocation,
    };
    frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
  }, [currentLocation, shellOrigin]);

  useEffect(() => {
    let lastSent = displayedColorScheme();
    const observer = new MutationObserver(() => {
      const scheme = displayedColorScheme();
      if (scheme === lastSent || !booted.current) return;
      lastSent = scheme;
      const message: IntercomThemeMessage = {
        type: "3b-intercom-theme",
        colorScheme: scheme,
      };
      frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => observer.disconnect();
  }, [shellOrigin]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== shellOrigin) return;
      if (
        !frameRef.current ||
        event.source !== frameRef.current.contentWindow
      ) {
        return;
      }
      const data = event.data as
        | IntercomReadyMessage
        | IntercomVisibilityMessage
        | IntercomUnreadMessage
        | undefined;
      if (data?.type === "3b-intercom-ready") {
        shellReady.current = true;
        if (readyVerdictTimer.current !== null) {
          clearTimeout(readyVerdictTimer.current);
          readyVerdictTimer.current = null;
          if (openRef.current) postShow();
        }
        if (shellFailed.current) {
          shellFailed.current = false;
          onFailedChange(false);
        }
        // An older shell omits appId; that is unknown, not a mismatch.
        if (
          typeof data.appId === "string" &&
          config.intercomAppId !== undefined &&
          data.appId !== config.intercomAppId &&
          !appIdMismatchLogged.current
        ) {
          appIdMismatchLogged.current = true;
          console.error(
            "Intercom app id mismatch between the UI bundle and the public shell",
            { uiAppId: config.intercomAppId, shellAppId: data.appId }
          );
        }
      } else if (data?.type === "3b-intercom-visibility") {
        const isVisible = data.visible === true;
        setVisible(isVisible);
        if (!isVisible) onClose();
      } else if (
        data?.type === "3b-intercom-unread" &&
        typeof data.count === "number"
      ) {
        onUnreadChange(data.count);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [shellOrigin, onClose, onUnreadChange, onFailedChange, postShow]);

  useEffect(() => {
    return () => {
      if (readyVerdictTimer.current !== null) {
        clearTimeout(readyVerdictTimer.current);
      }
    };
  }, []);

  const boot = useCallback(() => {
    const message: IntercomBootMessage = {
      type: "3b-intercom-boot",
      email: user.email,
      name: user.name,
      userJwt: intercomUserJwt(),
      company: { id: user.tenantId, name: window.location.host },
      path: locationRef.current,
      colorScheme: displayedColorScheme(),
    };
    frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
    booted.current = true;
    if (shellReady.current) {
      if (openRef.current) postShow();
      return;
    }
    // No ready message means either the shell script never ran (a 404, or a
    // public service without Intercom configured) or its message is still in
    // flight; the grace period tells the two apart.
    if (readyVerdictTimer.current !== null) {
      clearTimeout(readyVerdictTimer.current);
    }
    readyVerdictTimer.current = setTimeout(() => {
      readyVerdictTimer.current = null;
      shellFailed.current = true;
      console.warn(
        "Intercom support shell did not respond; falling back to the contact link",
        { shellUrl }
      );
      onFailedChange(true);
      if (openRef.current) {
        emitErrorToast({
          icon: "circle-off",
          message: "Support chat couldn’t load.",
          buttonLabel: "Dismiss",
        });
        onClose();
      }
    }, INTERCOM_READY_GRACE_MS);
  }, [
    user.email,
    user.name,
    user.tenantId,
    shellOrigin,
    shellUrl,
    postShow,
    onFailedChange,
    onClose,
  ]);

  return (
    <Frame
      ref={frameRef}
      src={shellUrl}
      title="Support"
      sandbox={intercomIframeSandbox}
      onLoad={boot}
      style={
        visible ? undefined : { visibility: "hidden", pointerEvents: "none" }
      }
    />
  );
}
